import { useMonthlyPayment } from "app/hooks/checkout/useMonthlyPayment";
import { CartProviderProps } from "app/providers/CartProvider";
import { OrderProviderProps } from "app/providers/OrderProvider";
import { useMemo } from "react";
import {
  CreateCartPayload,
  GetOrderResponse,
} from "shared/api/middleware-api-client";
import Text from "shared/ui/typography/Text";
import { concatClassnames } from "shared/utils/concatClassnames";
import MonthlyPayment from "widgets/shared/monthly-payment";
import { usePaymentContext } from "pages/payment/providers/PaymentProvider";
import { PaymentActions } from "shared/utils/payment-methods";
import { getFormattedPrice } from "shared/utils/getFormattedPrice";
interface ProductsTotalProps {
  className?: string;
  entity: CreateCartPayload | GetOrderResponse;
  context: OrderProviderProps | CartProviderProps;
  hideMonthlyPayment?: boolean;
  isSampleSale?: boolean;
  isBusiness?: boolean;
}

const ProductsTotal = ({
  className = "",
  context,
  entity,
  hideMonthlyPayment = false,
  isSampleSale = false,
  isBusiness = false,
}: ProductsTotalProps) => {
  const { paymentAction } = usePaymentContext();
  const rawTotalPrice =
    "method" in context
      ? context.getRawTotalPrice(context.method)
      : context.getRawTotalPrice();
  const canShowMonthlyPayment =
    !hideMonthlyPayment && !isSampleSale && !isBusiness;
  const { data } = useMonthlyPayment(rawTotalPrice, canShowMonthlyPayment);
  const monthlyPayment = data?.monthlyPayment;
  const showMonthlyPayment = canShowMonthlyPayment && (monthlyPayment ?? 0) > 0;

  // Total text to display
  const totalText = "Totaal incl. BTW";

  // Total price
  const totalPrice = context.getTotalPrice();

  // Calculate the VAT amount based on the total price
  const vatAmount =
    "getTotalPriceUnformatted" in context
      ? ((context.getTotalPriceUnformatted() * 21) / 121).toFixed(2)
      : null;

  // Determine if the pay now section should be shown
  const showPayNowSection =
    entity.products.length > 0 && !isSampleSale && !isBusiness;

  const paymentAmountNow = useMemo(() => {
    if ("getTotalRemainingAmount" in context) {
      let totalAmount = context.getTotalRemainingAmount();

      //Get 10% of the total amount if the payment action is pay now
      if (paymentAction === PaymentActions.PAY_NOW) {
        totalAmount = (totalAmount * 10) / 100;
      }
      return getFormattedPrice(totalAmount);
    } else {
      return context.getHumanReadableTotalRemaining();
    }
  }, [context, paymentAction]);

  return (
    <div
      className={concatClassnames(
        className,
        "gg-mt-5 xl:gg-ml-auto xl:gg-mt-2",
      )}
    >
      <Text className="gg-mb-1 gg-text-lg">{totalText}</Text>

      <Text
        data-testid="total-price"
        className="gg-text-3xl sm:gg-text-4xl"
        bold
      >
        {totalPrice}
      </Text>

      {showMonthlyPayment && <MonthlyPayment monthlyPayment={monthlyPayment} />}

      {showPayNowSection && (
        <Text className="gg-text-faded-white gg-mt-4 gg-text-sm">
          Nu te betalen:{" "}
          <span className="gg-font-bold">{paymentAmountNow}</span>
        </Text>
      )}

      {isBusiness && (
        <p className="gg-text-faded-white gg-mt-1 gg-text-xs">
          incl. €{vatAmount} btw
        </p>
      )}
    </div>
  );
};
export default ProductsTotal;
